import { createContext, useContext, useEffect, useRef } from 'react';

/**
 * The live hub's context (live/hub.js), provided by LiveProvider.jsx around
 * the signed-in Workspace. Kept apart from the provider, as
 * businessDay/BusinessDayContext.js is, so a screen imports the hook without
 * the provider's browser wiring.
 *
 * Outside a LiveProvider (a test rendering one screen, the server-side render
 * check) the context is null and useLiveTopic does nothing: the screen works
 * exactly as before, on its Refresh button.
 */
export const LiveContext = createContext(null);

/** The hub, or null outside a LiveProvider. */
export const useLiveHub = () => useContext(LiveContext);

/**
 * Re-read a screen's data whenever anyone changes it (docs/live-sync/
 * implementation-plan.md 4.6). `reload` is the screen's existing loader - the
 * one its Refresh button calls - so a live update is exactly a Refresh the
 * person did not have to press, with the same loading state, the same error
 * handling and the same filters kept.
 *
 *   useLiveTopic(['orders'], reload);                       // any fund
 *   useLiveTopic(['prices'], reload, { fundId: 'UTEQ' });   // one fund's hints (and fund-less ones)
 *   useLiveTopic(['orders'], reload, { enabled: open });     // only while a drawer is open
 *
 * The hub decides WHEN (debounced, only while the person is active, once
 * after a reconnect - live/hub.js); this hook only says WHAT the screen shows.
 * The latest `reload` is always the one called, so a screen may pass a fresh
 * function every render without resubscribing.
 *
 * @param {string[]} topics  live/topics.js TOPIC_NAMES the screen shows
 * @param {() => *} reload    the screen's loader
 * @param {{ fundId?: string|null, enabled?: boolean }} [options]
 */
export function useLiveTopic(topics, reload, { fundId = null, enabled = true } = {}) {
    const hub = useLiveHub();
    const latest = useRef(reload);
    latest.current = reload;
    // A stable key for the topic list, so a new array literal each render does not resubscribe.
    const key = [...topics].sort().join(',');

    useEffect(() => {
        if (!hub || !enabled || !key) return undefined;
        return hub.subscribe({ topics: key.split(','), fundId, reload: () => latest.current?.() });
    }, [hub, key, fundId, enabled]);
}
